import { useState } from "react";
import {
  Box, Typography, Paper, Stack, Alert, Chip,
  Dialog, DialogContent, DialogActions, Button,
  List, ListItem, ListItemText, Divider,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import { rolesApi, type RoleDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";

export function RolesPage() {
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["roles"], queryFn: () => rolesApi.list(),
  });

  const detail = useQuery({
    queryKey: ["role", selectedRoleId],
    queryFn: () => rolesApi.get(selectedRoleId!) as Promise<{
      id: string; name: string; code: string; isSystem: boolean;
      permissions: { id: string; code: string; module: string; resource: string; action: string; description?: string }[];
    }>,
    enabled: !!selectedRoleId,
  });

  // Group permissions by module for display
  const grouped = detail.data
    ? detail.data.permissions.reduce((acc, p) => {
      (acc[p.module] ??= []).push(p);
      return acc;
    }, {} as Record<string, typeof detail.data.permissions>)
    : null;

  const columns: GridColDef<RoleDto>[] = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
    { field: "code", headerName: "Code", width: 180 },
    {
      field: "isSystem", headerName: "System role", width: 140,
      renderCell: (p) => p.value ? <Chip size="small" color="info" label="System" /> : null,
    },
    { field: "permissionCount", headerName: "Permissions", width: 130, type: "number" },
  ];

  return (
    <Box>
      <PageHeader breadcrumbs={["Administration"]} title="Roles" />
      <Paper sx={{ p: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Roles are seeded by the system. Click a row to view its permissions. Custom role management is a future feature.
        </Alert>
        {list.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(list.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={list.data ?? []}
            columns={columns}
            loading={list.isLoading}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowClick={(p) => setSelectedRoleId(p.row.id)}
          />
        </Box>
      </Paper>

      <Dialog open={!!selectedRoleId} onClose={() => setSelectedRoleId(null)} maxWidth="sm" fullWidth>
        <ClosableDialogTitle onClose={() => setSelectedRoleId(null)}>
          {detail.data?.name ?? "Role"}
          {detail.data?.isSystem && <Chip size="small" color="info" label="System" sx={{ ml: 1 }} />}
        </ClosableDialogTitle>
        <DialogContent dividers>
          {detail.isLoading && <Stack alignItems="center" sx={{ py: 4 }}><Typography>Loading...</Typography></Stack>}
          {grouped && Object.entries(grouped).map(([module, perms], idx) => (
            <Box key={module} sx={{ mb: 2 }}>
              {idx > 0 && <Divider />}
              <Typography variant="subtitle2" sx={{ mt: idx === 0 ? 0 : 2, mb: 1, textTransform: "capitalize" }}>
                {module}
              </Typography>
              <List dense disablePadding>
                {perms.map(p => (
                  <ListItem key={p.id} disableGutters>
                    <ListItemText
                      primary={<code>{p.code}</code>}
                      secondary={p.description}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedRoleId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
